/**
 * simple helper that will try to run a closure and rethrow a mapped error.
 * useful for wrapping functions that throw errors and want to map them to a specific error type
 */
export function mapErrAndThrow(closure, mapErr) {
  try {
    return closure();
  } catch (e) {
    throw mapErr(e);
  }
}

/**
 * simple helper that will create an error with a message and a cause
 */
export function ErrorFrom(message, cause) {
  return new Error(message, {
    cause
  });
}

/**
 * Maps data if it exists, otherwise throws the provided error
 * @param data The data to map
 * @param mapper The mapping function
 * @param error The error to throw if data is null/undefined
 * @throws The provided error if data is null/undefined
 * @returns The mapped data
 */
export function mapIfExistsOrThrow(data, mapper, error) {
  if (data == null) {
    throw error;
  }
  return mapper(data);
}

/**
 * Maps data produced by a closure if it exists, otherwise throws the provided error
 * @param producer The closure that produces the data
 * @param mapper The mapping function
 * @param error The error to throw if data is null/undefined
 * @throws The provided error if data is null/undefined or if producer throws
 * @returns The mapped data
 */
export function mapIfExistsOrThrowFn(producer, mapper, error) {
  const data = producer();
  if (data == null) {
    throw error;
  }
  return mapper(data);
}

/**
 * Returns the fallback value if the function throws, otherwise returns the function's result
 * @param fallback The value to return if the function throws
 * @param fn The function to try
 * @returns The result of the function or the fallback value
 */
export function tryOr(fallback, fn) {
  try {
    return fn();
  } catch (e) {
    return fallback;
  }
}

/**
 * Returns the result of the function or the result of the else function if the function throws
 * @param fn The function to try
 * @param elseFn The function to return if the function throws
 * @returns The result of the function or the result of the else function
 *
 * Note: This helper function can be used to log errors and return a default value.
 * @example
 * ```ts
 * const result = tryOrElse(
 *   () => {
 *     throw new Error('Failed to fetch data');
 *   },
 *   (e) => {
 *     console.error(e); // Error is logged
 *     return 'Default value';
 *   }
 * );
 * ```
 */
export function tryOrElse(fn, elseFn) {
  try {
    return fn();
  } catch (e) {
    return elseFn(e);
  }
}